/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, gridClasses } from '@mui/material';
import ModalDialog from '../ui/ModalDialog/index';
import { CancelPresentationRounded, ContentCopyOutlined, DeleteRounded, EditOutlined, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import { csvFileToArray } from '../../utils/csvFileToArray';
import styles from './style';
import DrillFluidDetails from './drillFluidDetails';
import { addNewDrillFluidRow, copyDrillFluidRow, createDrillFluid, deleteDrillFluidRow, importDrillFluidDetails, listDrillFluid, updateDrillFluid } from '../../store/actions/wellsDrillFluid';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import { REDUX_ACTIONS } from '../../shared/config/constants';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import useUnitConversion from '../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';
import { checkPermissions } from '../../utils/checkPermissions';
import { APP_ACTION_TYPES } from '../../utils/constants';
const DrillFluidModal = ({
  onCloseClick,
  drillFluidData,
  isOpen
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const drillFluid = useSelector((state) => state.wellsDrillFluid);
  const apiRef = useGridApiRef();
  const fileReader = new FileReader();
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({});
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);

  useEffect(() => {
    if (!drillFluid.loading) {
      dispatch(listDrillFluid(drillFluidData?.row?._id, onCloseClick));
    }
  }, []);

  useEffect(() => {
    if (drillFluid?.data) {
      setProperties(drillFluid?.data);
    }
  }, [drillFluid?.data]);

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddDrillFluid = () => {
    dispatch(addNewDrillFluidRow());
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {drillFluidData?.row?.name || ''} | Drilling Fluid
          </Grid>
          <Grid item>
          <IconButton
              onClick={() => {
                if (unSavedExists) {
                  handleClose(false);
                  return;
                }
                onCloseClick();
              }}
            >
              <CancelPresentationRounded fontSize='large'/>
            </IconButton>
          </Grid>
        </Grid>
    );
  };

  const removeDrillFliud = (row) => {
    if (row?.isNewRow) {
      const updatedDrillFluid = properties.filter(item => item._id !== row?._id);
      setProperties(updatedDrillFluid);
      dispatch(listDrillFluid(drillFluidData?.row?._id));
    } else {
      dispatch(deleteDrillFluidRow(row?._id, drillFluidData?.row?._id, drillFluidData?.row?.wellsInfoId));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const hasDuplicateMDAndName = (arr, row) => {
    const seen = new Map();
    for (const item of arr) {
      const key = `${Number(item.MD)}-${item.name?.toLowerCase()?.trim()}`;
      if (seen.has(key)) {
        dispatch(
          enqueueSnackbar(
            'Name and MD already exist',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }
      seen.set(key, true);
    }
    return false;
  };

  const checkDrillFluidValues = (value) => {
    const row = value;
    const invalidFields = [];
    if (!row?.name) invalidFields.push('Name');
    if (row?.MD === undefined || row?.MD === null || row?.MD === '') invalidFields.push('MD');

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const handleSave = (row) => {
    if (checkDrillFluidValues(row)) {
      if (!hasDuplicateMDAndName(properties, row)) {
        const convertedMD = unit !== UNIT_SYSTEM.US
          ? convertUnitSystem(row?.MD, drillFluid?.units?.MD.conversionFactor)
          : Number(row?.MD);
        const body = {
          ...row,
          MD: convertedMD

        };
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        if (row?.isNewRow) {
          dispatch(createDrillFluid(body, drillFluidData?.row?._id));
        } else {
          dispatch(updateDrillFluid({ ...row, MD: convertedMD, wellsInfoId: drillFluidData?.row?._id }));
        }
      }
    }
  };

  const renderActionsCell = ({ row }) => {
    const hasDirtyRow = properties.some(item => item?.isDirty === true || item?.isNewRow);
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={drillFluid?.loading || row?.MD === '' || hasDirtyRow || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.VIEW, row?.permissions))}
          onClick={() => {
            if (!row?.isNewRow) {
              setEditData(row);
              setEditDialog(true);
            }
          }}
        >
          <EditOutlined />
        </IconButton>
        <IconButton
          disabled={!row.isDirty || drillFluid?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, row?.permissions))}
          variant="solid"
          onClick={() => {
            handleSave(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          disabled={drillFluid?.loading || properties.some(item => item?.isDirty === true || item?.isNewRow) || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.CREATE, row?.permissions))}
          ariant="solid"
          onClick={() => {
            dispatch(copyDrillFluidRow(row));
          }}
        >
          <ContentCopyOutlined/>
        </IconButton>
        <IconButton
          disabled={drillFluid?.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, row?.permissions))}
          variant="solid"
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const uploadCSVFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      fileReader.onload = function (event) {
        const csvOutput = event.target.result;
        const drillFluidList = csvFileToArray(csvOutput);
        dispatch(importDrillFluidDetails(drillFluidList));
      };
      fileReader.readAsText(file);
    }
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderValueTextCell = (params) => {
    return <SelectEditInputTextField {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};
  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={() => {
        if (unSavedExists) {
          handleClose(false);
          return;
        }
        onCloseClick();
      }}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <UnsavedChangesModal />
      <Grid item className={classes.customTool} xs={1} justifyContent="space-between" sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, drillFluid?.permissions) && 'none !important' }}>
            <Grid item>
              <Typography variant='button' fontWeight='700' onClick={handleAddDrillFluid} className={drillFluid.loading || properties.some(item => item?.isDirty === true || item?.isNewRow) ? classes.disableIcon : ''}>+ Add</Typography>
            </Grid>
            <Grid item>
              <label className={classes.importLabel} htmlFor='getCSVFile'>Import</label>
              <input type='file' id='getCSVFile' accept={'.csv'} style={{ display: 'none' }} onChange={uploadCSVFile} />
            </Grid>
          </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            loading={drillFluid.loading}
            columns={columns(renderActionsCell, renderValueCell, renderValueTextCell, drillFluid.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1.2rem',
                fontWeight: '900 !important',
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            autoheight
            editMode="cell"
            height={'50vh'}
            getRowClassName={(params) => {
              const isInvalid = invalidRows[params.id];
              const isEditable = params?.row?.permissions?.update;
              return `${isInvalid ? classes.invalidRow : ''} ${!isEditable && !params?.row?.isNewRow ? classes.rowViewMode : ''}`;
            }}
            pageSize={100}
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
          />
        </Grid>
        {editDialog && (
        <DrillFluidDetails
          drillFluidData={drillFluidData}
          open={editDialog}
          handleClose={() => {
            setEditDialog(false);
            setEditData({});
            dispatch({
              type: REDUX_ACTIONS.WELLS_DRILL_FLUID_SET_ID,
              payload: {
                data: '',
              },
            });
          }}
          data={editData}
        />
        )}
          {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={removeDrillFliud}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
      </Grid>
    </ModalDialog>
  );
};
DrillFluidModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  drillFluidData: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
};
DrillFluidModal.defaultProps = {
  drillFluidData: {}
};
export default DrillFluidModal;
